import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { useToast } from '@/lib/hooks/useToast'
import { tenantService, type Tenant } from '@/lib/services/tenantService'
import { Copy, ExternalLink, Loader2 } from 'lucide-react'

interface AdminConsentUrlDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  tenant: Tenant
}

export function AdminConsentUrlDialog({
  open,
  onOpenChange,
  tenant,
}: AdminConsentUrlDialogProps) {
  const { t } = useTranslation()
  const { toast } = useToast()
  const [consentUrl, setConsentUrl] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(false)

  useEffect(() => {
    if (!open) {
      setConsentUrl(null)
      return
    }

    const loadConsentUrl = async () => {
      try {
        setIsLoading(true)
        const config = await tenantService.getAdminConsentConfig()
        if (!config.azureClientId) {
          toast({
            title: t('admin.tenants.error', 'Error'),
            description: t(
              'admin.tenants.azureClientIdNotConfigured',
              'Azure Client ID is not configured. Please: 1) Set AZURE_CLIENT_ID environment variable, 2) Restart the Flask API server, 3) Try again.'
            ),
            variant: 'destructive',
          })
          return
        }

        const url = tenantService.generateAdminConsentUrl(
          tenant.tenantId,
          config.azureClientId,
          config.adminConsentCallbackUrl,
          config.state
        )
        setConsentUrl(url)
      } catch {
        toast({
          title: t('admin.tenants.error', 'Error'),
          description: t(
            'admin.tenants.failedToGenerateConsentUrl',
            'Failed to generate admin consent URL due to: {error}',
          ),
          variant: 'destructive',
        })
      } finally {
        setIsLoading(false)
      }
    }

    loadConsentUrl()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, tenant.tenantId])

  const handleCopyUrl = async () => {
    if (!consentUrl) return

    try {
      await navigator.clipboard.writeText(consentUrl)
      toast({
        title: t('admin.tenants.copied', 'Copied'),
        description: t(
          'admin.tenants.urlCopiedToClipboard',
          'Admin consent URL copied to clipboard'
        ),
        variant: 'success',
      })
    } catch {
      toast({
        title: t('admin.tenants.error', 'Error'),
        description: t(
          'admin.tenants.failedToCopyUrl',
          'Failed to copy URL to clipboard'
        ),
        variant: 'destructive',
      })
    }
  }

  const handleOpenUrl = () => {
    if (consentUrl) {
      window.open(consentUrl, '_blank', 'noopener,noreferrer')
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>
            {t('admin.tenants.adminConsentUrl', 'Admin Consent URL')}
          </DialogTitle>
          <DialogDescription>
            {t(
              'admin.tenants.adminConsentUrlDescription',
              'Share this URL with the tenant admin at {{tenantName}} to grant Crisis Buddy permissions.',
              { tenantName: tenant.tenantName }
            )}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : consentUrl ? (
            <>
              <div className="space-y-2">
                <label className="text-sm font-medium">
                  {t('admin.tenants.consentUrl', 'Consent URL')}
                </label>
                <div className="flex gap-2">
                  <Input
                    readOnly
                    value={consentUrl}
                    className="font-mono text-xs"
                  />
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleCopyUrl}
                    title={t(
                      'admin.tenants.copyUrlToClipboard',
                      'Copy URL to clipboard'
                    )}
                  >
                    <Copy className="h-4 w-4" />
                  </Button>
                </div>
              </div>

              <div className="rounded-lg bg-blue-50 dark:bg-blue-950 p-4 space-y-2">
                <h4 className="font-medium text-sm text-blue-900 dark:text-blue-100">
                  {t('admin.tenants.instructions', 'Instructions')}
                </h4>
                <ol className="text-sm text-blue-800 dark:text-blue-200 space-y-1 list-decimal list-inside">
                  <li>
                    {t(
                      'admin.tenants.instructionStep1',
                      'Copy the URL above (use the copy button)'
                    )}
                  </li>
                  <li>
                    {t(
                      'admin.tenants.instructionStep2',
                      'Send it to the tenant admin at {{tenantName}}',
                      { tenantName: tenant.tenantName }
                    )}
                  </li>
                  <li>
                    {t(
                      'admin.tenants.instructionStep3',
                      'They visit the link and authenticate with their Azure AD admin account'
                    )}
                  </li>
                  <li>
                    {t(
                      'admin.tenants.instructionStep4',
                      'They click "Accept" to grant Crisis Buddy permissions'
                    )}
                  </li>
                  <li>
                    {t(
                      'admin.tenants.instructionStep5',
                      'The Admin Consent status will automatically update to "Granted"'
                    )}
                  </li>
                </ol>
              </div>

              <div className="flex gap-2 pt-4">
                <Button onClick={handleOpenUrl} className="flex-1">
                  <ExternalLink className="h-4 w-4 mr-2" />
                  {t(
                    'admin.tenants.openInBrowser',
                    'Open URL in Browser'
                  )}
                </Button>
                <Button
                  variant="outline"
                  onClick={() => onOpenChange(false)}
                  className="flex-1"
                >
                  {t('admin.tenants.close', 'Close')}
                </Button>
              </div>
            </>
          ) : (
            <div className="text-center py-8">
              <p className="text-muted-foreground">
                {t(
                  'admin.tenants.failedToLoadConsentUrl',
                  'Failed to load consent URL'
                )}
              </p>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  )
}
